import { FLAGS } from '@iheartjane/feature-flags';
import { getDisabledWizardCategories } from '@iheartjane/menu';
import { useEffect } from 'react';
import type { LoaderFunctionArgs } from 'react-router';
import {
  Outlet,
  useLoaderData,
  useMatches,
  useSearchParams,
} from 'react-router';

import { StoreBanner } from '@jane/bloom/components/communicationBanner/storeBanner.tsx';
import { Header } from '@jane/bloom/components/header';
import { MobileLoginCallout } from '@jane/bloom/components/header/prompt/mobileLoginCallout';
import { CategorySelector } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/categorySelector';
import { BloomWrapper } from '@jane/bloom/components/wrapper';
import { fetchDmFacets } from '@jane/bloom/dataAccess/dm/facets.server';
import { fetchJaneGoldConfig } from '@jane/bloom/dataAccess/marketplace/janeGoldConfig.server';
import { fetchLoyaltyPoints } from '@jane/bloom/dataAccess/marketplace/loyaltyPoints.server';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useHasStoreBanner } from '@jane/bloom/hooks/useHasStoreBanner';
import { usePrompt } from '@jane/bloom/hooks/usePrompt';
import { useRefreshCart } from '@jane/bloom/hooks/useRefreshCart';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { globalSubloader } from '@jane/bloom/loaders/globalSubloader.server';
import { isPromptVisible } from '@jane/bloom/sessions/prompt.server';
import { getUserIdentity } from '@jane/bloom/sessions/userIdentity.server';
import { initializeDMContext } from '@jane/bloom/utils/dm/dmContext.server';
import { ensureBloomMenu } from '@jane/bloom/utils/ensureBloomMenu.server';
import { BloomError } from '@jane/bloom/utils/errors/bloomError';
import { getShouldShowGold } from '@jane/bloom/utils/gold/getShouldShowGold';
import { isFeatureFlagsEnabled } from '@jane/bloom/utils/isFeatureFlagEnabled.server';
import { PROMPTS } from '@jane/bloom/utils/prompts';
import { SIGN_IN_PROMPT_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';
import {
  accountPages,
  authPages,
  pdpPages,
  plpPages,
} from '@jane/bloom/utils/routes/routeIds';
import { getCustomLanguage } from '@jane/bloom/viewModels/getCustomLanguage.server';
import { buildNavCategories } from '@jane/bloom/viewModels/navigation/buildNavCategories';

import styles from './styles.module.css';
import type { LoaderData } from './types';

export async function loader({ request }: LoaderFunctionArgs) {
  const headers = new Headers();
  const menu = await ensureBloomMenu(request);
  const { pinnedStore, shoppingPreferences } = await globalSubloader(
    request,
    menu
  );
  const customLanguage = getCustomLanguage(pinnedStore);

  const {
    dmContext: { sdk },
    headers: dmHeaders,
  } = await initializeDMContext(request);
  if (dmHeaders) {
    dmHeaders.forEach((value, key) => {
      headers.append(key, value);
    });
  }

  const storewideFacets = await fetchDmFacets(sdk, {
    storeId: shoppingPreferences.storeId,
  });

  const hasAiqIntegration = pinnedStore.crm_integration === 'alpineiq';

  if (!storewideFacets) {
    throw new BloomError({ message: 'Store facets were not fetched' });
  }

  const userIdentity = await getUserIdentity(request, menu.cookie_prefix);

  const loyaltyPoints = hasAiqIntegration
    ? await fetchLoyaltyPoints({
        jwtToken: userIdentity?.jwtToken,
        storeId: pinnedStore.id,
      })
    : null;

  const goldConfig = await fetchJaneGoldConfig();
  const shouldShowGold = getShouldShowGold({
    goldConfig,
    store: pinnedStore,
    userSegments: userIdentity?.user_segments,
  });

  const facetsWithGold = {
    ...storewideFacets['kind'],
    ...(storewideFacets['has_brand_discount']
      ? {
          brand_specials: storewideFacets['has_brand_discount']['true'],
        }
      : {}),
  };

  let displayCategories = buildNavCategories({
    kindFacet: shouldShowGold ? facetsWithGold : storewideFacets['kind'],
    menuTabs: pinnedStore.menu_tabs,
    productTypeLabels: customLanguage.productTypeLabels,
  });

  if (!shouldShowGold) {
    displayCategories = displayCategories.filter(
      (category) => category.label !== 'Jane Gold'
    );
  }

  const disabledWizardCategories = getDisabledWizardCategories({
    kindFacet: storewideFacets['kind'],
    kindRootSubtypeFacet: storewideFacets['kind_root_subtype'],
  });

  const isLoggedIn = userIdentity?.user && 'authenticated' in userIdentity.user;
  const loginPromptVisible = isPromptVisible(
    request,
    PROMPTS.login,
    menu.cookie_prefix
  );
  const flags = await isFeatureFlagsEnabled({
    flagNames: [FLAGS.c1CustomizeLoginPrompt],
    request,
  });
  // c1CustomizeLoginPrompt gates the broader rollout: when off, the prompt
  // remains AIQ-only (matches master). When on, it shows for all premium
  // menus and AIQ stores still get their loyalty-pitch default.
  const customizeEnabled = flags[FLAGS.c1CustomizeLoginPrompt];
  const loaderData: LoaderData = {
    disabledWizardCategories,
    displayCategories,
    janeGold: {
      config: goldConfig,
      userIdentity,
    },
    loyaltyPoints,
    shouldShowLoginPrompt:
      !isLoggedIn &&
      loginPromptVisible &&
      (customizeEnabled || hasAiqIntegration),
  };

  return loaderData;
}

export default function MenuLayout() {
  const { displayCategories, janeGold, loyaltyPoints, shouldShowLoginPrompt } =
    useLoaderData<LoaderData>();
  const { cookiePrefix } = useRootLoaderData();
  const { pinnedStore } = useStoreSwitcher();
  useRefreshCart();

  const [isPromptVisible, dismissLoginPrompt] = usePrompt({
    cookiePrefix,
    dismissible: true,
    prompt: PROMPTS.login,
  });
  const [searchParams] = useSearchParams();
  const signInPromptPreview =
    searchParams.get(SIGN_IN_PROMPT_QUERY_KEY) === 'true';

  const pageMatches = useMatches();
  const accountPage = Boolean(
    pageMatches.find(({ id }) => accountPages.includes(id))
  );
  const isAuthPage = Boolean(
    pageMatches.find(({ id }) => authPages.includes(id))
  );
  const isPlpPage = Boolean(
    pageMatches.find(({ id }) => plpPages.includes(id))
  );
  const isPdpPage = Boolean(
    pageMatches.find(({ id }) => pdpPages.includes(id))
  );
  const shouldShowClosedStoreBanner =
    pinnedStore && !pinnedStore.openNow && (isPdpPage || isPlpPage);
  // signInPromptPreview bypasses both the server-side gate
  // (shouldShowLoginPrompt, which itself checks !isLoggedIn + prompt cookie)
  // and the client-side cookie check, so Business Admin can preview their
  // pending theme_draft regardless of login state or dismissed-prompt state.
  const showLoginPrompt =
    (isPdpPage || isPlpPage) &&
    (signInPromptPreview || (shouldShowLoginPrompt && isPromptVisible));
  const shouldShowLoyaltyBanner =
    loyaltyPoints && loyaltyPoints.loyalty_points > 0;

  const { setHasStoreBanner } = useHasStoreBanner();
  useEffect(() => {
    setHasStoreBanner(
      Boolean(shouldShowClosedStoreBanner) || Boolean(shouldShowLoyaltyBanner)
    );
  }, [shouldShowClosedStoreBanner, shouldShowLoyaltyBanner, setHasStoreBanner]);

  const showBanner =
    Boolean(shouldShowClosedStoreBanner) || Boolean(shouldShowLoyaltyBanner);

  return (
    <BloomWrapper>
      <Header
        borderless={accountPage || showBanner}
        disableAccountRedirect={isAuthPage}
        dismissLoginPrompt={showLoginPrompt ? dismissLoginPrompt : undefined}
        janeGold={janeGold}
        showLoginPrompt={showLoginPrompt}
      />
      {showBanner && pinnedStore && (
        <StoreBanner
          pinnedStore={pinnedStore}
          isStoreClosed={Boolean(shouldShowClosedStoreBanner)}
          loyaltyPoints={loyaltyPoints?.loyalty_points}
        />
      )}
      <div className={styles['layout']}>
        {showLoginPrompt && (
          <MobileLoginCallout onDismiss={dismissLoginPrompt} />
        )}
        {isPlpPage && (
          <CategorySelector displayCategories={displayCategories} />
        )}
        <Outlet />
      </div>
    </BloomWrapper>
  );
}
